package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.attributes.*
import com.mencanhealth.admin.data.AdminAppointmentService
import com.mencanhealth.admin.data.AppointmentStats
import com.mencanhealth.admin.model.*
import kotlinx.coroutines.launch
import kotlin.js.Date

@Composable
fun AppointmentManagementPage() {
    val scope = rememberCoroutineScope()
    val service = remember { AdminAppointmentService() }
    var appointments by remember { mutableStateOf<List<Appointment>>(emptyList()) }
    var stats by remember { mutableStateOf(AppointmentStats()) }
    var loading by remember { mutableStateOf(true) }
    var filter by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var selectedAppointment by remember { mutableStateOf<Appointment?>(null) }
    var meetLinkInput by remember { mutableStateOf("") }
    var editingMeetLinkId by remember { mutableStateOf<String?>(null) }
    var processing by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        try {
            appointments = service.listAppointments()
            stats = service.getAppointmentStats()
        } catch (e: Exception) {
            errorMessage = "Error cargando citas: ${e.message}"
        }
        loading = false
    }

    fun refresh() {
        scope.launch {
            try {
                appointments = service.listAppointments()
                stats = service.getAppointmentStats()
            } catch (e: Exception) {
                errorMessage = "Error actualizando: ${e.message}"
            }
        }
    }

    val filtered = appointments.filter { appt ->
        filter == null || appt.status == filter
    }

    Div({ classes("page") }) {

        // Detail view
        selectedAppointment?.let { appt ->
            AppointmentDetailView(
                appointment = appt,
                service = service,
                onBack = { selectedAppointment = null },
                onRefresh = {
                    refresh()
                    selectedAppointment = null
                },
                onMessage = { msg -> successMessage = msg },
                onError = { msg -> errorMessage = msg }
            )
            return@Div
        }

        H2 { Text("Gestión de Citas") }

        // Messages
        successMessage?.let { msg ->
            Div({
                classes("success-text")
                style { property("margin-bottom", "16px") }
            }) { Text(msg) }
        }
        errorMessage?.let { msg ->
            Div({
                classes("error-banner")
                style { property("margin-bottom", "16px") }
            }) { Text(msg) }
        }

        // Stats bar
        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "16px")
                property("margin-bottom", "24px")
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            StatCard("Hoy", stats.todayCount.toString(), "#1565C0")
            StatCard("Pendientes de Pago", stats.pendingPayment.toString(), "#E65100")
            StatCard("Confirmadas", stats.confirmed.toString(), "#2E7D32")
            StatCard("Este Mes", stats.monthTotal.toString(), "#6A1B9A")
        }

        // Filter chips
        Div({ classes("toolbar") }) {
            Div({ classes("filter-chips") }) {
                Button({
                    classes("chip"); if (filter == null) classes("chip-active")
                    onClick { filter = null }
                }) { Text("Todas") }
                listOf(
                    AppointmentStatus.PENDING_PAYMENT to "Pendientes de Pago",
                    AppointmentStatus.CONFIRMED to "Confirmadas",
                    AppointmentStatus.COMPLETED to "Completadas",
                    AppointmentStatus.CANCELLED to "Canceladas",
                    AppointmentStatus.NO_SHOW to "No Asistió"
                ).forEach { (status, label) ->
                    Button({
                        classes("chip"); if (filter == status.name) classes("chip-active")
                        onClick { filter = status.name }
                    }) { Text(label) }
                }
            }
        }

        if (loading) {
            P { Text("Cargando citas...") }
        } else if (filtered.isEmpty()) {
            Div({ classes("empty-state") }) { Text("No hay citas") }
        } else {
            Div({ classes("user-count") }) { Text("${filtered.size} citas") }
            Table({ classes("data-table") }) {
                Thead {
                    Tr {
                        Th { Text("Paciente") }
                        Th { Text("Tipo") }
                        Th { Text("Fecha") }
                        Th { Text("Hora") }
                        Th { Text("Modalidad") }
                        Th { Text("Estado") }
                        Th { Text("Acciones") }
                    }
                }
                Tbody {
                    filtered.forEach { appt ->
                        Tr({
                            classes("clickable-row")
                            onClick { selectedAppointment = appt }
                        }) {
                            Td { Text(appt.patientName) }
                            Td {
                                Text(try {
                                    AppointmentType.valueOf(appt.appointmentType).label
                                } catch (_: Exception) { appt.appointmentType })
                            }
                            Td { Text(formatAppointmentDate(appt.date)) }
                            Td { Text(appt.timeSlot) }
                            Td {
                                Text(try {
                                    AppointmentModality.valueOf(appt.modality).label
                                } catch (_: Exception) { appt.modality })
                            }
                            Td { AppointmentStatusBadge(appt.status) }
                            Td({
                                onClick { it.stopPropagation() }
                            }) {
                                when (appt.status) {
                                    AppointmentStatus.PENDING_PAYMENT.name -> {
                                        Button({
                                            classes("btn-small")
                                            style { property("margin-right", "4px"); property("background", "#1565C0"); color(Color.white) }
                                            if (processing) attr("disabled", "")
                                            onClick {
                                                processing = true
                                                scope.launch {
                                                    try {
                                                        service.confirmPayment(appt.id)
                                                        refresh()
                                                        successMessage = "Pago confirmado para ${appt.patientName}"
                                                        errorMessage = null
                                                    } catch (e: Exception) {
                                                        errorMessage = "Error: ${e.message}"
                                                    }
                                                    processing = false
                                                }
                                            }
                                        }) { Text("Confirmar Pago") }
                                        Button({
                                            classes("btn-small", "btn-secondary")
                                            if (processing) attr("disabled", "")
                                            onClick {
                                                processing = true
                                                scope.launch {
                                                    try {
                                                        service.cancelAppointment(appt.id)
                                                        refresh()
                                                        successMessage = "Cita cancelada"
                                                        errorMessage = null
                                                    } catch (e: Exception) {
                                                        errorMessage = "Error: ${e.message}"
                                                    }
                                                    processing = false
                                                }
                                            }
                                        }) { Text("Cancelar") }
                                    }
                                    AppointmentStatus.CONFIRMED.name -> {
                                        Button({
                                            classes("btn-small")
                                            style { property("margin-right", "4px"); property("background", "#2E7D32"); color(Color.white) }
                                            if (processing) attr("disabled", "")
                                            onClick {
                                                processing = true
                                                scope.launch {
                                                    try {
                                                        service.markCompleted(appt.id)
                                                        refresh()
                                                        successMessage = "Cita completada"
                                                        errorMessage = null
                                                    } catch (e: Exception) {
                                                        errorMessage = "Error: ${e.message}"
                                                    }
                                                    processing = false
                                                }
                                            }
                                        }) { Text("Completada") }
                                        Button({
                                            classes("btn-small")
                                            style { property("margin-right", "4px"); property("background", "#c62828"); color(Color.white) }
                                            if (processing) attr("disabled", "")
                                            onClick {
                                                processing = true
                                                scope.launch {
                                                    try {
                                                        service.markNoShow(appt.id)
                                                        refresh()
                                                        successMessage = "Marcada como No Asistió"
                                                        errorMessage = null
                                                    } catch (e: Exception) {
                                                        errorMessage = "Error: ${e.message}"
                                                    }
                                                    processing = false
                                                }
                                            }
                                        }) { Text("No Asistió") }
                                        Button({
                                            classes("btn-small", "btn-secondary")
                                            style { property("margin-right", "4px") }
                                            if (processing) attr("disabled", "")
                                            onClick {
                                                processing = true
                                                scope.launch {
                                                    try {
                                                        service.cancelAppointment(appt.id)
                                                        refresh()
                                                        successMessage = "Cita cancelada"
                                                        errorMessage = null
                                                    } catch (e: Exception) {
                                                        errorMessage = "Error: ${e.message}"
                                                    }
                                                    processing = false
                                                }
                                            }
                                        }) { Text("Cancelar") }
                                        if (appt.modality == AppointmentModality.ONLINE.name) {
                                            if (editingMeetLinkId == appt.id) {
                                                Input(InputType.Text) {
                                                    value(meetLinkInput)
                                                    attr("placeholder", "https://meet.google.com/...")
                                                    onInput { meetLinkInput = it.value }
                                                    style { property("width", "180px"); property("margin-right", "4px") }
                                                }
                                                Button({
                                                    classes("btn-small")
                                                    style { property("background", "#1565C0"); color(Color.white) }
                                                    onClick {
                                                        scope.launch {
                                                            try {
                                                                service.setMeetLink(appt.id, meetLinkInput)
                                                                refresh()
                                                                successMessage = "Meet link guardado"
                                                                errorMessage = null
                                                                editingMeetLinkId = null
                                                                meetLinkInput = ""
                                                            } catch (e: Exception) {
                                                                errorMessage = "Error: ${e.message}"
                                                            }
                                                        }
                                                    }
                                                }) { Text("Guardar") }
                                            } else {
                                                Button({
                                                    classes("btn-small")
                                                    style { property("background", "#00897B"); color(Color.white) }
                                                    onClick {
                                                        editingMeetLinkId = appt.id
                                                        meetLinkInput = appt.meetLink
                                                    }
                                                }) { Text("Meet Link") }
                                            }
                                        }
                                    }
                                    else -> {
                                        Text("-")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StatCard(title: String, value: String, color: String) {
    Div({
        style {
            property("background", color)
            color(Color.white)
            property("border-radius", "8px")
            property("padding", "16px 24px")
            property("min-width", "140px")
            property("flex", "1")
            textAlign("center")
        }
    }) {
        Div({
            style { property("font-size", "28px"); property("font-weight", "700") }
        }) { Text(value) }
        Div({
            style { property("font-size", "13px"); property("opacity", "0.9"); property("margin-top", "4px") }
        }) { Text(title) }
    }
}

@Composable
private fun AppointmentStatusBadge(status: String) {
    val (label, cssClass) = when (status) {
        AppointmentStatus.PENDING_PAYMENT.name -> "Pendiente de Pago" to "status-pending-payment"
        AppointmentStatus.CONFIRMED.name -> "Confirmada" to "status-confirmed"
        AppointmentStatus.COMPLETED.name -> "Completada" to "status-completed"
        AppointmentStatus.CANCELLED.name -> "Cancelada" to "status-cancelled"
        AppointmentStatus.NO_SHOW.name -> "No Asistió" to "status-no-show"
        else -> status to "status-badge"
    }
    Span({ classes("status-badge", cssClass) }) { Text(label) }
}

@Composable
private fun AppointmentDetailView(
    appointment: Appointment,
    service: AdminAppointmentService,
    onBack: () -> Unit,
    onRefresh: () -> Unit,
    onMessage: (String) -> Unit,
    onError: (String) -> Unit
) {
    val scope = rememberCoroutineScope()
    var meetLinkInput by remember { mutableStateOf(appointment.meetLink) }
    var processing by remember { mutableStateOf(false) }

    Button({
        classes("btn-back")
        onClick { onBack() }
    }) { Text("\u2190 Volver a lista") }

    H2 { Text(appointment.patientName) }

    Div({ classes("detail-card") }) {
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Email") }
            Span { Text(appointment.patientEmail) }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("WhatsApp") }
            if (appointment.patientWhatsApp.isNotBlank()) {
                A(
                    href = "https://wa.me/${appointment.patientWhatsApp.replace(Regex("[^0-9]"), "")}",
                    attrs = { attr("target", "_blank") }
                ) { Text(appointment.patientWhatsApp) }
            } else {
                Span { Text("-") }
            }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Tipo") }
            Span {
                Text(try {
                    AppointmentType.valueOf(appointment.appointmentType).label
                } catch (_: Exception) { appointment.appointmentType })
            }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Modalidad") }
            Span {
                Text(try {
                    AppointmentModality.valueOf(appointment.modality).label
                } catch (_: Exception) { appointment.modality })
            }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Fecha") }
            Span { Text(formatAppointmentDate(appointment.date)) }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Hora") }
            Span { Text(appointment.timeSlot) }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Duración") }
            Span { Text("${appointment.durationMinutes} min") }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Estado") }
            AppointmentStatusBadge(appointment.status)
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Método de Pago") }
            Span {
                Text(try {
                    PaymentMethod.valueOf(appointment.paymentMethod).label
                } catch (_: Exception) { appointment.paymentMethod })
            }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Pago Confirmado") }
            Span { Text(if (appointment.paymentConfirmed) "Sí" else "No") }
        }
        if (appointment.meetLink.isNotBlank()) {
            Div({ classes("detail-row") }) {
                Span({ classes("detail-label") }) { Text("Meet Link") }
                A(
                    href = appointment.meetLink,
                    attrs = { attr("target", "_blank") }
                ) { Text(appointment.meetLink) }
            }
        }
        if (appointment.notes.isNotBlank()) {
            Div({ classes("detail-row") }) {
                Span({ classes("detail-label") }) { Text("Notas") }
                Span { Text(appointment.notes) }
            }
        }
        Div({ classes("detail-row") }) {
            Span({ classes("detail-label") }) { Text("Creada") }
            Span { Text(formatTimestamp(appointment.createdAt)) }
        }
        if (appointment.confirmedAt > 0) {
            Div({ classes("detail-row") }) {
                Span({ classes("detail-label") }) { Text("Confirmada") }
                Span { Text(formatTimestamp(appointment.confirmedAt)) }
            }
        }
    }

    // Questionnaire responses
    if (appointment.questionnaireResponses.isNotBlank()) {
        QuestionnaireResponsesViewer(
            appointmentType = appointment.appointmentType,
            responsesJson = appointment.questionnaireResponses
        )
    }

    // Actions section
    when (appointment.status) {
        AppointmentStatus.PENDING_PAYMENT.name -> {
            H3 { Text("Acciones") }
            Div({ style { display(DisplayStyle.Flex); property("gap", "8px") } }) {
                Button({
                    classes("btn-small")
                    style { property("background", "#1565C0"); color(Color.white); property("padding", "8px 16px") }
                    if (processing) attr("disabled", "")
                    onClick {
                        processing = true
                        scope.launch {
                            try {
                                service.confirmPayment(appointment.id)
                                onMessage("Pago confirmado para ${appointment.patientName}")
                                onRefresh()
                            } catch (e: Exception) {
                                onError("Error: ${e.message}")
                                processing = false
                            }
                        }
                    }
                }) { Text("Confirmar Pago") }
                Button({
                    classes("btn-secondary")
                    style { property("padding", "8px 16px") }
                    if (processing) attr("disabled", "")
                    onClick {
                        processing = true
                        scope.launch {
                            try {
                                service.cancelAppointment(appointment.id)
                                onMessage("Cita cancelada")
                                onRefresh()
                            } catch (e: Exception) {
                                onError("Error: ${e.message}")
                                processing = false
                            }
                        }
                    }
                }) { Text("Cancelar Cita") }
            }
        }
        AppointmentStatus.CONFIRMED.name -> {
            H3 { Text("Acciones") }
            Div({ style { display(DisplayStyle.Flex); property("gap", "8px"); flexWrap(FlexWrap.Wrap) } }) {
                Button({
                    classes("btn-small")
                    style { property("background", "#2E7D32"); color(Color.white); property("padding", "8px 16px") }
                    if (processing) attr("disabled", "")
                    onClick {
                        processing = true
                        scope.launch {
                            try {
                                service.markCompleted(appointment.id)
                                onMessage("Cita completada")
                                onRefresh()
                            } catch (e: Exception) {
                                onError("Error: ${e.message}")
                                processing = false
                            }
                        }
                    }
                }) { Text("Completada") }
                Button({
                    classes("btn-small")
                    style { property("background", "#c62828"); color(Color.white); property("padding", "8px 16px") }
                    if (processing) attr("disabled", "")
                    onClick {
                        processing = true
                        scope.launch {
                            try {
                                service.markNoShow(appointment.id)
                                onMessage("Marcada como No Asistió")
                                onRefresh()
                            } catch (e: Exception) {
                                onError("Error: ${e.message}")
                                processing = false
                            }
                        }
                    }
                }) { Text("No Asistió") }
                Button({
                    classes("btn-secondary")
                    style { property("padding", "8px 16px") }
                    if (processing) attr("disabled", "")
                    onClick {
                        processing = true
                        scope.launch {
                            try {
                                service.cancelAppointment(appointment.id)
                                onMessage("Cita cancelada")
                                onRefresh()
                            } catch (e: Exception) {
                                onError("Error: ${e.message}")
                                processing = false
                            }
                        }
                    }
                }) { Text("Cancelar Cita") }
            }
            if (appointment.modality == AppointmentModality.ONLINE.name) {
                H3 { Text("Meet Link") }
                Div({ style { display(DisplayStyle.Flex); property("gap", "8px"); alignItems(AlignItems.Center) } }) {
                    Input(InputType.Text) {
                        classes("search-input")
                        value(meetLinkInput)
                        attr("placeholder", "https://meet.google.com/...")
                        onInput { meetLinkInput = it.value }
                        style { property("flex", "1") }
                    }
                    Button({
                        classes("btn-small")
                        style { property("background", "#00897B"); color(Color.white); property("padding", "8px 16px") }
                        onClick {
                            scope.launch {
                                try {
                                    service.setMeetLink(appointment.id, meetLinkInput)
                                    onMessage("Meet link guardado")
                                    onRefresh()
                                } catch (e: Exception) {
                                    onError("Error: ${e.message}")
                                }
                            }
                        }
                    }) { Text("Guardar") }
                }
            }
        }
    }
}

private fun formatAppointmentDate(dateStr: String): String {
    if (dateStr.isBlank()) return "-"
    // dateStr is "YYYY-MM-DD"
    val parts = dateStr.split("-")
    if (parts.size != 3) return dateStr
    return "${parts[2]}/${parts[1]}/${parts[0]}"
}

private fun formatTimestamp(timestamp: Long): String {
    if (timestamp == 0L) return "-"
    val d = Date(timestamp.toDouble())
    val day = d.getDate().toString().padStart(2, '0')
    val month = (d.getMonth() + 1).toString().padStart(2, '0')
    val year = d.getFullYear()
    val hours = d.getHours().toString().padStart(2, '0')
    val minutes = d.getMinutes().toString().padStart(2, '0')
    return "$day/$month/$year $hours:$minutes"
}
